import { memo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Search, Menu, Home, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCommandPaletteActions } from '@/features/command-palette';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface HeaderProps {
  onMenuClick?: () => void;
}

export const Header = memo(function Header({ onMenuClick }: HeaderProps) {
  const location = useLocation();
  const params = useParams();
  const { open: openCommandPalette } = useCommandPaletteActions();

  // Generate breadcrumbs from current path
  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const path = location.pathname;

    if (path === '/dashboard') {
      return [{ label: 'Dashboard' }];
    }

    if (path === '/doctors') {
      return [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Médicos' }];
    }

    if (path === '/doctors/new') {
      return [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Médicos', path: '/doctors' },
        { label: 'Nuevo Médico' },
      ];
    }

    if (path.match(/^\/doctors\/\d+$/)) {
      const doctorName = `Doctor #${params['id'] || ''}`;
      return [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Médicos', path: '/doctors' },
        { label: doctorName },
      ];
    }

    if (path.match(/^\/doctors\/\d+\/edit$/)) {
      return [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Médicos', path: '/doctors' },
        { label: 'Editar Médico' },
      ];
    }

    if (path === '/billing-cuts') {
      return [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Cortes de Facturación' }];
    }

    if (path.match(/^\/billing-cuts\/\d+$/)) {
      return [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Cortes de Facturación', path: '/billing-cuts' },
        { label: `Corte #${params['id'] || ''}` },
      ];
    }

    return [];
  };

  const breadcrumbItems = getBreadcrumbItems();

  return (
    <header className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Breadcrumbs - hidden on mobile, visible on lg+ */}
        {breadcrumbItems.length > 0 && (
          <nav className="hidden items-center space-x-2 lg:flex">
            <Link
              to="/dashboard"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 transition-all duration-200 hover:bg-[var(--blue-2)] hover:text-[var(--blue-11)] dark:text-gray-400 dark:hover:bg-[var(--blue-3)] dark:hover:text-[var(--blue-9)]"
              aria-label="Ir al dashboard"
            >
              <Home className="h-4 w-4" />
            </Link>

            {breadcrumbItems.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                {index === breadcrumbItems.length - 1 ? (
                  <div className="rounded-lg border border-[var(--blue-6)] bg-gradient-to-br from-[var(--blue-2)] to-[var(--blue-3)] px-3 py-1.5 text-sm font-semibold text-gray-900 dark:border-[var(--blue-7)] dark:from-[var(--blue-3)] dark:to-[var(--blue-4)] dark:text-gray-100">
                    {item.label}
                  </div>
                ) : item.path ? (
                  <Link
                    to={item.path}
                    className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-[var(--blue-2)] hover:text-[var(--blue-11)] dark:text-gray-300 dark:hover:bg-[var(--blue-3)] dark:hover:text-[var(--blue-9)]"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.label}
                  </span>
                )}
              </div>
            ))}
          </nav>
        )}

        {/* Search button - opens command palette */}
        <div className="hidden max-w-xl flex-1 md:flex">
          <button
            onClick={openCommandPalette}
            className="relative flex h-9 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400 transition-colors hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700"
          >
            <Search className="mr-2 h-4 w-4" />
            <span className="flex-1 text-left">Buscar médicos, trámites, documentos...</span>
            <kbd className="ml-2 hidden rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 lg:inline-block">
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Spacer for alignment when no search on mobile */}
        <div className="flex-1 md:hidden" />
      </div>

      {/* Mobile search button - visible only on mobile */}
      <div className="mt-3 md:hidden">
        <button
          onClick={openCommandPalette}
          className="relative flex h-9 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400 transition-colors hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700"
        >
          <Search className="mr-2 h-4 w-4" />
          <span className="flex-1 text-left">Buscar...</span>
        </button>
      </div>
    </header>
  );
});
